import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Trash2, Sparkles, Eye, EyeOff } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/blog/admin`;
const KEY_STORAGE = "shg_blog_admin_key";

const BlogAdminPage = () => {
  useSEO({ title: "Blog admin | Stagehook Global", description: "Internal", path: "/admin/blog" });
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const [posts, setPosts] = useState([]);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const call = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", "X-Admin-Key": key, ...(opts.headers || {}) } });
    if (!res.ok) throw new Error(res.status === 401 ? "Wrong admin key" : `Request failed (${res.status})`);
    return res.json();
  }, [key]);

  const load = useCallback(async () => {
    if (!key) return;
    try { setError(""); setPosts(await call("/posts")); sessionStorage.setItem(KEY_STORAGE, key); }
    catch (e) { setError(e.message); setPosts([]); }
  }, [key, call]);

  useEffect(() => { load(); }, [load]);

  const act = async (label, fn) => {
    setBusy(label); setError("");
    try { await fn(); await load(); } catch (e) { setError(e.message); } finally { setBusy(""); }
  };

  return (
    <main data-testid="blog-admin-page" className="pt-[100px] pb-24">
      <div className="shg-container max-w-5xl">
        <div className="shg-eyebrow mb-4">Internal · Blog drafts</div>
        <h1 className="font-display text-4xl md:text-5xl mb-8">Weekly article approvals</h1>

        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <input data-testid="admin-key" type="password" placeholder="Admin key" value={key} onChange={(e) => setKey(e.target.value)} className="flex-1 bg-transparent border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--red)]" style={{ borderColor: "var(--line-strong)" }} />
          <button data-testid="admin-load" onClick={load} className="btn-ghost">Load drafts</button>
        </div>

        <div className="rounded-2xl p-6 mb-10" style={{ background: "var(--surface)", border: "1px solid var(--line-strong)" }}>
          <div className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] uppercase mb-3">Generate a draft now</div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input data-testid="admin-topic" placeholder="Topic (leave blank to pick the next from the queue)" value={topic} onChange={(e) => setTopic(e.target.value)} className="flex-1 bg-transparent border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--red)]" style={{ borderColor: "var(--line-strong)" }} />
            <button data-testid="admin-generate" disabled={!!busy || !key} onClick={() => act("generate", () => call("/generate", { method: "POST", body: JSON.stringify({ topic: topic || null }) }))} className="btn-primary disabled:opacity-60">
              <Sparkles size={15} /> {busy === "generate" ? "Writing… (≈60s)" : "Generate draft"}
            </button>
          </div>
          <p className="text-[11px] text-[var(--text-muted)] mt-3">A new draft is also generated automatically every Monday 09:00 UTC. Nothing goes live until you click Publish.</p>
        </div>

        {error && <p data-testid="admin-error" className="text-sm text-[var(--red)] mb-6">{error}</p>}

        <div className="space-y-4">
          {posts.map((p) => (
            <div key={p.id} data-testid={`admin-post-${p.slug}`} className="shg-card p-5 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] uppercase mb-2">
                  <span style={{ color: p.status === "published" ? "#4ade80" : "var(--red)" }}>{p.status}</span>
                  <span className="text-[var(--text-muted)]">{p.category} · {p.date}</span>
                </div>
                <div className="font-display text-lg leading-tight">{p.title}</div>
                <div className="text-xs text-[var(--text-muted)] mt-1 truncate">/blog/{p.slug}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {p.status === "published" && <Link to={`/blog/${p.slug}`} className="btn-ghost text-xs py-2 px-3">View <ArrowUpRight size={13} /></Link>}
                {p.status === "published"
                  ? <button data-testid={`admin-unpublish-${p.slug}`} disabled={!!busy} onClick={() => act(p.id, () => call(`/posts/${p.id}/unpublish`, { method: "POST" }))} className="btn-ghost text-xs py-2 px-3"><EyeOff size={13} /> Unpublish</button>
                  : <button data-testid={`admin-publish-${p.slug}`} disabled={!!busy} onClick={() => act(p.id, () => call(`/posts/${p.id}/publish`, { method: "POST" }))} className="btn-primary text-xs py-2 px-3"><Eye size={13} /> Publish</button>}
                <button data-testid={`admin-delete-${p.slug}`} disabled={!!busy} onClick={() => window.confirm("Delete this draft?") && act(p.id, () => call(`/posts/${p.id}`, { method: "DELETE" }))} className="btn-ghost text-xs py-2 px-3 hover:border-[var(--red)]"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
          {key && !posts.length && !error && <p className="text-sm text-[var(--text-muted)]">No drafts yet.</p>}
        </div>
      </div>
    </main>
  );
};

export default BlogAdminPage;
